import { Controller } from "@hotwired/stimulus"

// The series multi-select on the racing discover page.
//
// The rest of the filter bar is plain links, because those URLs are shareable
// and have to survive a Turbo "load more" without carrying client state. This
// one control is the exception: picking five series through links would be five
// page loads, so the selection accumulates here and navigates once on Done.
//
// The follow bells inside the panel are NOT part of that draft — they are real
// forms that post immediately, because following is a durable act rather than a
// filter you are still composing.
export default class extends Controller {
  static targets = [
    "panel",
    "query",
    "row",
    "group",
    "checkbox",
    "count",
    "label",
    "empty",
    "defaultField",
  ]
  static values = { selected: Array, baseUrl: String }

  connect() {
    this.selection = new Set(this.selectedValue)
    this.closeOnOutside = this.closeOnOutside.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnOutside)
    document.removeEventListener("keydown", this.closeOnEscape)
  }

  toggle(event) {
    event.stopPropagation()
    this.panelTarget.classList.contains("hidden") ? this.show() : this.hide()
  }

  show() {
    this.panelTarget.classList.remove("hidden")
    document.addEventListener("click", this.closeOnOutside)
    document.addEventListener("keydown", this.closeOnEscape)
    this.queryTarget.focus()
  }

  hide() {
    this.panelTarget.classList.add("hidden")
    document.removeEventListener("click", this.closeOnOutside)
    document.removeEventListener("keydown", this.closeOnEscape)
  }

  closeOnOutside(event) {
    if (!this.element.contains(event.target)) this.hide()
  }

  closeOnEscape(event) {
    if (event.key === "Escape") this.hide()
  }

  select(event) {
    const key = event.target.value
    event.target.checked ? this.selection.add(key) : this.selection.delete(key)
    this.refreshLabel()
    this.syncDefaultField()
  }

  clear() {
    this.selection.clear()
    this.checkboxTargets.forEach((box) => {
      box.checked = false
    })
    this.refreshLabel()
    this.syncDefaultField()
  }

  // Matches name, short name, sanctioning body and class keys — all of which
  // are in the row's data-search blob, so filtering never touches the DOM text.
  filter() {
    const term = this.queryTarget.value.trim().toLowerCase()
    let matches = 0

    this.rowTargets.forEach((row) => {
      const hit = !term || row.dataset.search.includes(term)
      row.classList.toggle("hidden", !hit)
      if (hit) matches += 1
    })

    // Hide a group whose every row was filtered out, so its heading rule does
    // not float above nothing.
    this.groupTargets.forEach((group) => {
      const anyVisible = Array.from(
        group.querySelectorAll("[data-racing-series-picker-target='row']"),
      ).some((row) => !row.classList.contains("hidden"))
      group.classList.toggle("hidden", !anyVisible)
    })

    this.emptyTarget.classList.toggle("hidden", matches > 0)
    if (this.hasCountTarget) {
      this.countTarget.textContent = `${matches} of ${this.rowTargets.length}`
    }
  }

  apply() {
    const url = new URL(this.baseUrlValue, window.location.origin)
    // An explicit empty `series` is meaningful: it says "I really do mean none",
    // so the server does not fall back to the reader's saved default.
    url.searchParams.set("series", Array.from(this.selection).join(","))
    url.searchParams.delete("scope")
    url.searchParams.delete("cursor")
    window.location.assign(url.toString())
  }

  refreshLabel() {
    if (!this.hasLabelTarget) return

    const size = this.selection.size
    this.labelTarget.textContent =
      size === 0 ? `All ${this.rowTargets.length} series` : `${size} selected`
  }

  // "Save as default" posts the draft, not the applied URL, so you can pick a
  // set and save it in one pass without navigating first.
  syncDefaultField() {
    if (!this.hasDefaultFieldTarget) return

    this.defaultFieldTarget.value = Array.from(this.selection).join(",")
  }
}
